import { Box, Drawer, IconButton, Toolbar, useTheme } from "@mui/material";
import { eLynxObjectTypeEnum } from "../../common/enums/eLynxObjectType";
import { NavigationSource } from "../../common/enums/NavigationSource";
import { NewViewTypeEnum } from "../../common/enums/NewViewTypeEnum";
import useNavigateToView from "../../hooks/use-navigate-to-view/UseNavigateToView";
import { IRoute } from "../../routes/routes";
import { INavigationItem } from "../../services/navigation-data-service/Models";
import NavigationGroupTree from "../navigation/NavigationGroupTree";
import NavigationObjectList from "../navigation/NavigationObjectList";
import PushPinButton from "../push-pin-button/PushPinButton";
import { useRef } from "react";
import useAutoScroll from "../../hooks/use-auto-scroll/useAutoScroll";

export type ContextualNavDrawerProps = {
    open: boolean;
    item: INavigationItem;
    route: IRoute;
    pinned?: boolean;
    onPinClick?: (() => void) | undefined;
    onClose?: () => void;
}

export default function ContextualNavDrawer({
    open,
    item,
    route,
    pinned,
    onPinClick,
    onClose
}: ContextualNavDrawerProps) {

    const theme = useTheme();
    const navigateToView = useNavigateToView();
    const containerRef = useRef<HTMLDivElement>(null);

    useAutoScroll(open, containerRef);
    
    return (
        <Drawer
            sx={{
                height: 1,
                overflow: "hidden",
            }}
            variant="permanent"
            open={open}
            slotProps={{
                paper: {
                    sx: {
                        position: "relative",
                        whiteSpace: "nowrap",
                        maxWidth: theme.spacing(50),
                        [theme.breakpoints.down('xs')]: {
                            maxWidth: '100%'
                        },
                        transition: theme.transitions.create("width", {
                            easing: theme.transitions.easing.sharp,
                            duration: open ? theme.transitions.duration.enteringScreen : theme.transitions.duration.leavingScreen,
                        }),
                        width: !open ? theme.spacing(7) : 'auto',
                        height: 1,
                        overflow: "hidden"
                    }
                }
            }}
        >
            <Toolbar />
            <Box
                sx={{
                    [theme.breakpoints.up("sm")]: {
                        width: theme.spacing(50),
                    },
                    position: 'relative',
                    height: 1,
                    overflow: "hidden",
                }}
            >
                <Box
                    sx={{
                        height: 1,
                        overflow: "hidden"
                    }}
                    ref={containerRef}
                >
                    {item && item?.objectTypeID === eLynxObjectTypeEnum.Group &&
                        <NavigationGroupTree
                            id='contextual-grp-tree'
                            route={route}
                            actions={
                                <PushPinButton onClick={onPinClick} pinned={pinned} />
                            }
                            onClick={(item, _, shiftKey, ctrlKey) => {
                                if (!pinned) {
                                    onClose?.();
                                }

                                navigateToView(
                                    item.objectTypeID,
                                    item.objectID,
                                    undefined,
                                    NavigationSource.ContextualNavDrawer,
                                    (shiftKey || ctrlKey)
                                        ? {
                                            newViewType: shiftKey
                                                ? NewViewTypeEnum.NewWindow
                                                : NewViewTypeEnum.NewTab
                                        }
                                        : undefined
                                )
                            }}
                        />
                    }

                    {item && item?.objectTypeID !== eLynxObjectTypeEnum.Group &&
                        <NavigationObjectList
                            item={item}
                            route={route}
                            offline={false}
                            enabled={false}
                            actions={
                                <PushPinButton onClick={onPinClick} pinned={pinned} />
                            }
                            onClick={(item, route, shiftKey, ctrlKey) => {
                                if (!pinned) {
                                    onClose?.();
                                }

                                navigateToView(
                                    item.objectTypeID,
                                    item.objectID,
                                    route?.viewID,
                                    NavigationSource.ContextualNavDrawer,
                                    (shiftKey || ctrlKey)
                                        ? {
                                            newViewType: shiftKey
                                                ? NewViewTypeEnum.NewWindow
                                                : NewViewTypeEnum.NewTab
                                        }
                                        : undefined
                                )
                            }}
                        />
                    }
                </Box>
            </Box>
        </Drawer>
    );
}